import { BaseService } from "./baseService";
import { Task, TaskListResponse, Attachment } from "../types";
import { createUserFilter } from "../utils/apiHelpers";
import apiClient from "./api";
import {
  generateCacheKey,
  staleWhileRevalidate,
} from "../storage/cacheManager";
import {
  requestDeduplication,
  generateRequestKey,
} from "../utils/requestDeduplication";

class TaskService extends BaseService<Task> {
  constructor() {
    super("Task");
    this.setCacheStrategy("stale-while-revalidate");
    this.setCacheExpiry(30 * 60 * 1000); // 30 минут
  }

  async getTasks(userId: string, offset = 0): Promise<TaskListResponse> {
    const selectFields = [
      "id",
      "name",
      "description",
      "resolution",
      "cSolution",
      "assignedUserId",
      "assignedUserName",
      "group",
      "dateStart",
      "dateEnd",
      "dateCompleted",
      "dateClosed",
      "status",
      "attachmentsIds",
      "attachmentsNames",
      "attachmentsTypes",
    ];

    return this.getList({
      select: selectFields,
      where: [createUserFilter(userId)],
      offset,
      orderBy: "name",
      order: "asc",
    });
  }

  async getTaskById(taskId: string): Promise<Task> {
    return this.getById(taskId);
  }

  async createTask(taskData: Partial<Task>): Promise<Task> {
    const payload: Partial<Task> = { ...taskData };
    delete payload.attachments;

    const storedAttachments: Attachment[] = JSON.parse(
      localStorage.getItem("pendingAttachments") || "[]",
    );

    if (storedAttachments.length > 0) {
      payload.attachmentsIds = storedAttachments.map((a) => a.id);
      payload.attachmentsNames = storedAttachments.reduce(
        (acc, a) => ({ ...acc, [a.id]: a.name }),
        {} as Record<string, string>,
      );
      payload.attachmentsTypes = storedAttachments.reduce(
        (acc, a) => ({ ...acc, [a.id]: a.type || "" }),
        {} as Record<string, string>,
      );
    }

    const result = await this.create(payload);
    const { invalidateListCache } = await import("../storage/cacheManager");
    await invalidateListCache("tasks");
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("tasks:changed"));
    }
    return result;
  }

  async updateTask(taskId: string, updatedData: Partial<Task>): Promise<Task> {
    const url = `/Task/${taskId}`;
    const response = await apiClient.put<Task>(url, updatedData);

    const { invalidateListCache } = await import("../storage/cacheManager");
    await invalidateListCache("tasks");

    await this.invalidateCache(taskId);
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("tasks:changed"));
    }

    return response.data;
  }
  async deleteTask(taskId: string): Promise<void> {
    await this.delete(taskId);
    const { invalidateListCache } = await import("../storage/cacheManager");
    await invalidateListCache("tasks");
    await this.invalidateCache(taskId);
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("tasks:changed"));
    }
  }
}

export const taskService = new TaskService();

export type { Task, TaskListResponse };
export const getTasks = (userId: string, offset = 0) =>
  taskService.getTasks(userId, offset);
export const getTaskById = (taskId: string) => taskService.getTaskById(taskId);
export const createTask = (taskData: Partial<Task>) =>
  taskService.createTask(taskData);
export const updateTask = (taskId: string, updatedData: Partial<Task>) =>
  taskService.updateTask(taskId, updatedData);
export const deleteTask = (taskId: string) => taskService.deleteTask(taskId);
